/**
 * OpenAPI 3.1 Spec Generator
 *
 * Converts a DomainDefinitionJSON into an OpenAPI 3.1 specification.
 * Maps ArkType-compatible type expressions to JSON Schema, resources
 * to path items, and authz to security schemes.
 */

import { scope, type Module, type Type, type JsonSchema as ArkJsonSchema } from "arktype";
import type { DomainDefinitionJSON } from "../schema/domain-definition";
import { coreDomain } from "../runtime/domain-runtime";
import { domainPrimitives } from "./compile-types";

// ---------------------------------------------------------------------------
// OpenAPI 3.1 types (subset)
// ---------------------------------------------------------------------------

export interface OpenAPISpec {
  openapi: "3.1.0";
  info: { title: string; version: string; description?: string };
  paths: Record<string, PathItem>;
  components: {
    // Component schemas are produced natively by ArkType's `Type.toJsonSchema()`
    // (draft-2020-12, an OpenAPI 3.1 superset), so they carry the full constraint
    // set (`format`, `pattern`, `minimum`, `minLength`, `additionalProperties`, …)
    // that the former hand-rolled string parser dropped.
    schemas: Record<string, ArkJsonSchema>;
    securitySchemes?: Record<string, SecurityScheme>;
  };
  security?: Array<Record<string, string[]>>;
}

interface PathItem {
  [method: string]: Operation;
}

interface Operation {
  operationId: string;
  summary?: string;
  parameters?: Parameter[];
  responses: Record<string, ResponseObject>;
  security?: Array<Record<string, string[]>>;
  tags?: string[];
}

interface Parameter {
  name: string;
  in: "path" | "query" | "header";
  required: boolean;
  schema: JsonSchema;
}

interface ResponseObject {
  description: string;
  content?: Record<string, { schema: JsonSchema }>;
}

interface SecurityScheme {
  type: string;
  scheme?: string;
  bearerFormat?: string;
  flows?: Record<string, unknown>;
}

export interface JsonSchema {
  type?: string | string[];
  properties?: Record<string, JsonSchema>;
  required?: string[];
  items?: JsonSchema;
  enum?: string[];
  minLength?: number;
  $ref?: string;
  additionalProperties?: boolean;
  oneOf?: JsonSchema[];
  description?: string;
}

// ---------------------------------------------------------------------------
// Native ArkType → JSON Schema conversion (compiled Type → schema)
// ---------------------------------------------------------------------------

/**
 * Convert a compiled ArkType `Type` to JSON Schema via ArkType's own
 * `toJsonSchema()`, the single source of truth for constraint fidelity.
 *
 * `dialect: null` omits the `$schema` key (component schemas are embedded, not
 * standalone documents). Cross-type references inline (no `$ref`) — every named
 * domain type still appears as its own `components.schemas` entry because the
 * caller iterates `def.types`.
 *
 * The `fallback` map handles constructs with no JSON Schema representation,
 * degrading to the closest serializable shape instead of throwing (ArkType's
 * default). `morph` emits the OUTPUT schema — the shape an API actually returns.
 */
function typeToOpenApiSchema(t: Type): ArkJsonSchema {
  return t.toJsonSchema({
    dialect: null,
    fallback: {
      // Pipes / `.parse` morphs: emit the output shape the API returns.
      morph: (ctx) => ctx.out ?? {},
      // `Date` → an ISO date-time string.
      date: () => ({ type: "string", format: "date-time" }),
      // Non-JSON domains (symbol / bigint / undefined) → best-effort string.
      domain: () => ({ type: "string" }),
      // Drop the un-serializable refinement, keep the representable base.
      unit: (ctx) => ctx.base,
      proto: (ctx) => ctx.base,
      predicate: (ctx) => ctx.base,
      defaultValue: (ctx) => ctx.base,
      arrayObject: (ctx) => ctx.base,
      arrayPostfix: (ctx) => ctx.base,
      patternIntersection: (ctx) => ctx.base,
      symbolKey: (ctx) => ctx.base,
    },
  });
}

/**
 * Compile a domain's `types` map into a ArkType `Module` when the caller did not
 * already build one. The real pipeline (`compileDomain`) always passes its
 * compiled module; this fallback keeps the standalone `compileOpenAPI(def)`
 * signature working, resolving `coreDomain` + platform primitives so
 * `EntityId` / `Money` / `Quantity` and friends still convert.
 */
function compileTypesForOpenApi(types: Record<string, unknown>): Module {
  return scope({ ...coreDomain, ...domainPrimitives, ...types }).export();
}

// ---------------------------------------------------------------------------
// Legacy ArkType expression → JSON Schema string parser (DEPRECATED)
// ---------------------------------------------------------------------------

/** Known primitive types that map directly to JSON Schema */
const PRIMITIVES: Record<string, JsonSchema> = {
  string: { type: "string" },
  number: { type: "number" },
  boolean: { type: "boolean" },
  integer: { type: "integer" },
};

/** All type names defined in the domain (unused by the native path). */
let knownTypes: Set<string> = new Set();

/**
 * @deprecated Lossy string-expression parser retained only for the public
 * export surface. `compileOpenAPI` now derives component schemas natively via
 * {@link typeToOpenApiSchema} (`Type.toJsonSchema()`), which captures the
 * constraints (`format`, `pattern`, `minimum`, `minLength`, …) this parser
 * drops. Prefer converting a compiled `Type` directly.
 *
 * Handles:
 * - Primitives: "string", "boolean", "number"
 * - Constrained strings: "string > 0" → minLength: 1
 * - Nullable unions: "string | null" → type: ["string", "null"]
 * - String enums: "'draft' | 'refined' | 'production'" → enum
 * - Array types: "string[]", "ValuePublic[]"
 * - Type references: "OrganizationId" → $ref
 * - UUID: "string.uuid" → { type: "string", format: "uuid" }
 * - Integer constraints: "number.integer >= 0" → { type: "integer", minimum: 0 }
 */
export function arkTypeToJsonSchema(expr: unknown): JsonSchema {
  if (typeof expr === "string") {
    return stringExprToSchema(expr);
  }

  if (typeof expr === "object" && expr !== null && !Array.isArray(expr)) {
    return objectToSchema(expr as Record<string, unknown>);
  }

  return {};
}

function stringExprToSchema(expr: string): JsonSchema {
  const trimmed = expr.trim();

  // Array type: "string[]" or "TypeName[]"
  if (trimmed.endsWith("[]")) {
    const itemType = trimmed.slice(0, -2);
    return { type: "array", items: arkTypeToJsonSchema(itemType) };
  }

  // String enum: "'draft' | 'refined' | 'production'"
  if (trimmed.includes("'") && trimmed.includes("|")) {
    const values = trimmed
      .split("|")
      .map((s) => s.trim().replace(/^'|'$/g, ""))
      .filter((s) => s.length > 0);
    return { type: "string", enum: values };
  }

  // Nullable union: "string | null", "TypeName | null"
  if (trimmed.includes("| null")) {
    const baseType = trimmed.replace(/\|\s*null/, "").trim();
    const baseSchema = arkTypeToJsonSchema(baseType);
    // OpenAPI 3.1 supports type as array for nullable
    if (baseSchema.type && typeof baseSchema.type === "string") {
      return { ...baseSchema, type: [baseSchema.type, "null"] };
    }
    if (baseSchema.$ref) {
      return { oneOf: [baseSchema, { type: "null" }] };
    }
    return baseSchema;
  }

  // UUID: "string.uuid"
  if (trimmed === "string.uuid") {
    return { type: "string", description: "UUID" };
  }

  // Integer with constraint: "number.integer >= 0"
  if (trimmed.startsWith("number.integer")) {
    return { type: "integer" };
  }

  // Constrained string: "string > 0"
  if (trimmed.startsWith("string") && trimmed.includes(">")) {
    const match = trimmed.match(/string\s*>\s*(\d+)/);
    if (match?.[1] !== undefined) {
      return { type: "string", minLength: parseInt(match[1], 10) + 1 };
    }
  }

  // Primitives
  const primitive = PRIMITIVES[trimmed];
  if (primitive) {
    return { ...primitive };
  }

  // Type reference (known domain type)
  if (knownTypes.has(trimmed)) {
    return { $ref: `#/components/schemas/${trimmed}` };
  }

  // Fallback: treat as string
  return { type: "string" };
}

function objectToSchema(obj: Record<string, unknown>): JsonSchema {
  const properties: Record<string, JsonSchema> = {};
  const required: string[] = [];

  for (const [key, value] of Object.entries(obj)) {
    // Skip ArkType meta keys like "+"
    if (key === "+") continue;

    // Optional keys end with "?" in ArkType
    const isOptional = key.endsWith("?");
    const cleanKey = isOptional ? key.slice(0, -1) : key;

    properties[cleanKey] = arkTypeToJsonSchema(value);
    if (!isOptional) {
      required.push(cleanKey);
    }
  }

  return {
    type: "object",
    properties,
    required: required.length > 0 ? required : undefined,
    additionalProperties: obj["+"] === "reject" ? false : undefined,
  };
}

// ---------------------------------------------------------------------------
// URL template → OpenAPI path + parameters
// ---------------------------------------------------------------------------

/** Extract path parameters from URL template like "/api/bible/{organizationId}/summary" */
function extractPathParams(urlTemplate: string): Parameter[] {
  const params: Parameter[] = [];
  const regex = /\{(\w+)\}/g;
  let match: RegExpExecArray | null;

  while ((match = regex.exec(urlTemplate)) !== null) {
    const [, name] = match;
    if (name === undefined) continue;
    params.push({
      name,
      in: "path",
      required: true,
      schema: { type: "string" },
    });
  }

  return params;
}

/** OpenAPI already uses {param} format, so no conversion needed */
function normalizeUrlTemplate(urlTemplate: string): string {
  return urlTemplate;
}

// ---------------------------------------------------------------------------
// Security scheme generation
// ---------------------------------------------------------------------------

function buildSecuritySchemes(
  def: DomainDefinitionJSON
): Record<string, SecurityScheme> {
  const schemes: Record<string, SecurityScheme> = {};

  // Check if any resource requires authentication
  const hasProtected = Object.values(def.resources).some(
    (r) => r.authz.visibility !== "public"
  );

  if (hasProtected) {
    schemes.bearerAuth = {
      type: "http",
      scheme: "bearer",
      bearerFormat: "JWT",
    };
  }

  return schemes;
}

// ---------------------------------------------------------------------------
// Main compiler
// ---------------------------------------------------------------------------

/**
 * Compile a DomainDefinitionJSON into an OpenAPI 3.1 specification.
 *
 * @param types - The domain's already-compiled ArkType `Module` (from
 *   `compileDomain`). Component schemas are derived from it natively via
 *   `Type.toJsonSchema()`. When omitted, a best-effort module is compiled from
 *   `def.types` (+ `coreDomain` / platform primitives) so standalone callers
 *   keep working.
 */
export function compileOpenAPI(
  def: DomainDefinitionJSON,
  types?: Module,
): OpenAPISpec {
  // Derive component schemas natively from the compiled types. Iterate the
  // domain's declared type names (not the whole module, which also carries
  // core + injected primitives) so `components.schemas` keys stay real type
  // names. Cross-type references inline; every named type still gets an entry.
  const module = (types ?? compileTypesForOpenApi(def.types)) as unknown as Record<string, Type>;
  const schemas: Record<string, ArkJsonSchema> = {};
  for (const name of Object.keys(def.types)) {
    const compiledType = module[name];
    if (compiledType) {
      schemas[name] = typeToOpenApiSchema(compiledType);
    }
  }

  // Build paths from resources
  const paths: Record<string, PathItem> = {};

  for (const [key, resource] of Object.entries(def.resources)) {
    const path = normalizeUrlTemplate(resource.fetch.urlTemplate);
    const method = resource.fetch.method.toLowerCase();
    const parameters = extractPathParams(resource.fetch.urlTemplate);

    const operation: Operation = {
      operationId: `${key}.${method}`,
      summary: `${method.toUpperCase()} ${key}`,
      tags: [def.name],
      parameters: parameters.length > 0 ? parameters : undefined,
      responses: {
        "200": {
          description: `${key} resource`,
          content: {
            "application/json": {
              schema: { $ref: `#/components/schemas/${resource.type}` },
            },
          },
        },
      },
    };

    // Add request body for POST/PUT
    if (resource.fetch.method === "POST" || resource.fetch.method === "PUT") {
      operation.summary = `${resource.fetch.method === "POST" ? "Create" : "Update"} ${key}`;
    }

    // Add security requirements for non-public resources
    if (resource.authz.visibility !== "public") {
      const scopes = resource.authz.scopes ?? [];
      operation.security = [{ bearerAuth: scopes }];

      // Add 401/403 responses
      operation.responses["401"] = {
        description: "Authentication required",
      };
      operation.responses["403"] = {
        description: "Insufficient permissions",
      };
    }

    if (!paths[path]) {
      paths[path] = {};
    }
    paths[path][method] = operation;
  }

  // Build security schemes
  const securitySchemes = buildSecuritySchemes(def);

  return {
    openapi: "3.1.0",
    info: {
      title: `${def.name} API`,
      version: def.version,
      description: def.description,
    },
    paths,
    components: {
      schemas,
      securitySchemes:
        Object.keys(securitySchemes).length > 0
          ? securitySchemes
          : undefined,
    },
  };
}
